import './OrdersList.css';
import React, { useState } from 'react';
import { Order, colorForOrderStatus, OrderStatus } from '../../../../model/order';
import { SortableList, ColumnContent } from '../../../../components/SortableList/SortableList';
import { orderPath, picklistPath } from '../../../../routes';
import { formatDateTime, formatCurrency, useErrorHandler } from '../../../../util/util';
import { Tag } from '../../../../components/Tag/Tag';
import { camelCase, compact } from 'lodash';
import { TableBlock } from '../../../../components/TableBlock/TableBlock';
import { Icon } from '../../../../components/Icon/Icon';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { UpdateOrderTag } from './UpdateOrderTag';
import { NavLink } from 'react-router-dom';
import { camelize } from 'humps';
import { EditOrderUser } from './AddUsersToOrder';
import { t } from '../../../../locales/translate';
import { PaymentModel, deletePayment, bookPayment, paymentMethodsById, Payment } from '../../../../model/payment';
import { DocumentCell } from '../OrderDetails/OrderItemsRow/Documents';
import { useRemoteData } from '../../../../model/data';
import { getPicklist } from '../../../../model/picklist';
import { cacheRequest } from '../../../../model/cache';
import { EditCancelAt } from './EditCancelAt';
import { Tooltip } from '../../../../components/Tooltip/Tooltip';
import { ShopName } from '../../../../components/ShopName/ShopName';
import { SortByIcon } from '../../../../components/SortableList/SortByIcon';
import { handleSuccess } from '../../../../model/notification';
import { RefundPaymentModal } from './RefundPaymentModal';
import { PaymentModal } from '../modals/PaymentModal';
import { PermissionButton } from '../../../../components/Button/PermissionButton';
import { Stack } from '../../../../components/Stack/Stack';
import { useModalPresenter } from '../../../../components/Modal/PresentedModal';
import { CapturePaymentModal } from './paypal_authorization/capture_payment_modal';
import { ReleasePaymentModal } from './paypal_authorization/release_payment_modal';

interface Props {
  orders: Order[];
  selectedOrders?: Order[];
  error?: Error;
  loadingTable?: boolean;
  loadingMore?: boolean;
  actions?: React.ReactNode;
  addPaymentColumn?: boolean;
  displayRowActions?: boolean;
  showBalance?: boolean;
  displayDeleteDocuments?: boolean;
  hideUser?: boolean;
  onRowSelect?: (item: Order) => void;
  onSelectAll?: () => void;
  onDocumentChange?: () => void;
}

function copyPaymentLinkToClipboard(row: Order): void {
  if (row.paymentLink) {
    navigator.clipboard.writeText(row.paymentLink);
    handleSuccess(t.model.order.copyPaymentLinkConfirmation);
  }
}

export function OrdersList({
  orders,
  selectedOrders,
  error,
  loadingTable,
  loadingMore,
  actions,
  onRowSelect,
  onSelectAll,
  onDocumentChange,
  displayRowActions = true,
  displayDeleteDocuments,
  addPaymentColumn = true,
  showBalance = false,
  hideUser,
}: Props): JSX.Element {
  const modal = useModalPresenter();
  const [selectedOrder, setSelectedOrder] = useState<Order | undefined>(undefined);
  const [selectedOrderPayment, setSelectedOrderPayment] = useState<Payment>();
  const [displayTagsModal, setDisplayTagsModal] = useState(false);
  const [displayUsersModal, setDisplayUsersModal] = useState(false);
  const [displayPaymentModal, setDisplayPaymentModal] = useState<'create' | 'edit'>();
  const [displayEditCancelAt, setDisplayEditCancelAt] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const selectedPayment = orders[0]?.payments?.find((p) => p.id === selectedPaymentId) ?? null;

  function editTags(row: Order): void {
    setDisplayTagsModal(true);
    setSelectedOrder(row);
  }

  function DeliveryNode({
    trackingUrl,
    picklistId,
  }: {
    trackingUrl?: string;
    picklistId?: string;
  }): JSX.Element | null {
    if (trackingUrl) {
      return (
        <a className="button button--link" href={trackingUrl} target="_blank" rel="noopener noreferrer">
          {t.model.delivery.trackingLink}
        </a>
      );
    }
    if (picklistId) {
      return <PicklistNode picklistId={picklistId} />;
    }
    return null;
  }

  function PicklistNode({ picklistId }: { picklistId: string }): JSX.Element | null {
    const [picklist, , error] = useRemoteData(
      () => cacheRequest(`getPickList-${picklistId}`, () => getPicklist(picklistId), 1000 * 60 * 60 * 24 * 7),
      (db) => db.table('picklists').find(picklistId)
    );
    useErrorHandler(error);
    if (!picklist) {
      return null;
    }
    return (
      <NavLink className="button button--link" to={picklistPath(picklistId)}>
        {picklist.reference}
      </NavLink>
    );
  }

  async function handleDeletePayment(orderId: string, id: string): Promise<void> {
    await deletePayment(orderId, id);
  }

  return (
    <>
      <SortableList
        className="orders-list"
        items={orders}
        rowLink={(order) => orderPath(order)}
        error={error}
        loading={loadingTable}
        showLoadMoreSpinner={loadingMore}
        actions={actions}
        onRowSelect={onRowSelect}
        selectedItems={selectedOrders}
        onSelectAll={onSelectAll}
        verticalAlign="top"
        multiSelect={selectedOrders !== undefined}
        columns={[
          {
            dataKey: 'reference',
            label: t.model.order.orderId,
            apiSortIcon: <SortByIcon sortBy="created_at" />,
            cellRenderer: (order) => <TableBlock header={order.reference} footer={formatDateTime(order.createdAt)} />,
          },
          !hideUser && {
            dataKey: 'user',
            label: t.model.user._name,
            cellRenderer: (order) => {
              if (order.user) {
                const name =
                  order.user.firstName && order.user.lastName ? `${order.user.firstName} ${order.user.lastName}` : '';
                return (
                  <TableBlock
                    header={name ? name : order.user.email}
                    footer={
                      name && (
                        <>
                          {order.user.email}
                          <br />
                          {order.shopId && <ShopName id={order.shopId} />}
                        </>
                      )
                    }
                  />
                );
              } else if (order.company) {
                return order.company.name;
              }
              return displayRowActions ? (
                <Icon
                  className="icon--gray"
                  name="user-plus"
                  onClick={() => {
                    setDisplayUsersModal(true);
                    setSelectedOrder(order);
                  }}
                />
              ) : null;
            },
          },
          {
            dataKey: 'invoices',
            label: t.model.invoice._plural,
            disableLink: true,
            cellRenderer: (row) =>
              row.invoices.map((invoice) => (
                <DocumentCell
                  key={invoice.id}
                  order={row}
                  document={invoice}
                  documentType="invoice"
                  displayDate
                  displayContextMenu={false}
                  footer={
                    <>
                      <div>{compact([formatCurrency(invoice.total), invoice.bookingNumber]).join(', ')}</div>
                    </>
                  }
                  displayDelete={displayDeleteDocuments}
                  onChange={onDocumentChange}
                ></DocumentCell>
              )),
          },
          {
            dataKey: 'payments',
            label: t.model.payment._plural,
            apiSortIcon: <SortByIcon sortBy="last_payment_created_at" />,
            disableLink: true,
            cellRenderer: (row) => {
              if (row.payments.length === 0 && row.preferredPaymentMethod) {
                return t.model.bank[camelize(row.preferredPaymentMethod)];
              }
              return row.payments.map((payment) => {
                const paymentId = payment.id;
                return (
                  <TableBlock
                    icon={
                      <>
                        {payment.documentUrl && (
                          <a href={payment.documentUrl} target="_blank" rel="noopener noreferrer">
                            <Icon name="download--sm"></Icon>
                          </a>
                        )}
                        {payment.$permissions?.includes('refund') && displayRowActions && (
                          <Icon name="arrow-return" onClick={() => setSelectedPaymentId(payment.id!)}></Icon>
                        )}
                      </>
                    }
                    key={payment.id}
                    header={formatDateTime(payment.createdAt)}
                    footer={
                      <Stack line size="xxs">
                        {[
                          formatCurrency(payment.amount),
                          paymentMethodsById[payment.paymentMethod].name,
                          !payment.externalPaymentId ? t.model.payment.adminPayment : undefined,
                        ]
                          .filter(Boolean)
                          .join(', ')}{' '}
                        {payment.status !== 'paid' && `<${t.model.status[payment.status]}>`}
                        {payment.status === 'pending' && (
                          <>
                            <Icon
                              name="bank-notes"
                              title={t.model.payment.markAsPaid}
                              onClick={() => {
                                payment.id && bookPayment(row.id, payment.id);
                              }}
                            />
                            <Icon
                              name="trash--sm"
                              title={t.actions.delete('')}
                              onClick={() => {
                                payment.id && handleDeletePayment(row.id, payment.id);
                              }}
                            />
                            <PermissionButton
                              name="edit--sm"
                              permission="update"
                              onClick={() => {
                                setSelectedOrder(row);
                                setSelectedOrderPayment(payment);
                                setDisplayPaymentModal('edit');
                              }}
                              model={payment}
                              hideLock
                              size={18}
                            />
                            {paymentId && payment.authorizationProcessable && (
                              <>
                                <PermissionButton
                                  permission="update"
                                  name="capture"
                                  title={t.model.order.capturePayment.title}
                                  model={payment}
                                  onClick={() => {
                                    modal.present(
                                      CapturePaymentModal,
                                      { paymentId, onClose: onDocumentChange },
                                      { size: 'small' }
                                    );
                                  }}
                                  size={18}
                                />
                                <PermissionButton
                                  permission="update"
                                  name="release"
                                  title={t.model.order.releasePayment.title}
                                  model={payment}
                                  onClick={() => {
                                    modal.present(
                                      ReleasePaymentModal,
                                      { paymentId, onClose: onDocumentChange },
                                      { size: 'small' }
                                    );
                                  }}
                                  size={18}
                                />
                              </>
                            )}
                          </>
                        )}
                      </Stack>
                    }
                  />
                );
              });
            },
          },
          {
            dataKey: 'deliveries',
            label: t.model.delivery._plural,
            disableLink: true,
            cellRenderer: (row) =>
              row.deliveries.map((delivery) => (
                <DocumentCell
                  key={delivery.id}
                  order={row}
                  document={delivery}
                  documentType="delivery"
                  displayDate
                  displayContextMenu={false}
                  displayDelete={displayDeleteDocuments}
                  footer={<DeliveryNode trackingUrl={delivery.trackingUrl} picklistId={delivery.picklistId} />}
                />
              )),
          },
          ...(showBalance
            ? ([
                {
                  dataKey: 'balance',
                  label: t.model.order.balance,
                  cellRenderer: (row) =>
                    row.balance > 0 ? (
                      <>
                        <Tag
                          size="small"
                          text={`${formatCurrency(row.balance)}`}
                          backgroundColor="var(--color-tint)"
                          squared
                        />
                        {row.balance > 0 && (
                          <Icon
                            tooltip={t.model.order.copyPaymentLink}
                            name="copy"
                            onClick={() => copyPaymentLinkToClipboard(row)}
                          />
                        )}
                      </>
                    ) : (
                      formatCurrency(row.balance)
                    ),
                },
              ] as ColumnContent<Order>[])
            : []),

          {
            dataKey: 'status',
            label: t.model.status._name,
            cellRenderer: (row) => (
              <TableBlock
                header={
                  <Tag
                    size="small"
                    squared
                    text={t.model.status[camelCase(row.status)]}
                    backgroundColor={colorForOrderStatus(camelCase(row.status) as OrderStatus)}
                  />
                }
                footer={
                  row.status === 'payment_pending' && (
                    <TableBlock
                      header={
                        <div className="orders-list__auto-cancel">
                          {row.cancelAt ? (
                            <Tooltip
                              darkMode
                              position="top"
                              reference={<span>{formatDateTime(row.cancelAt)}</span>}
                              className="auto-cancel__tooltip"
                            >
                              {t.model.order.autoCancelAt}
                            </Tooltip>
                          ) : (
                            displayRowActions && <span>{t.model.order.autoCancelAt}</span>
                          )}
                        </div>
                      }
                      icon={displayRowActions && <Icon name="edit--sm" onClick={() => setDisplayEditCancelAt(true)} />}
                    />
                  )
                }
              />
            ),
          },
          {
            dataKey: 'tags',
            label: t.model.order.tags,
            disableLink: true,
            cellRenderer: (row) => {
              return (
                <TableBlock
                  header={
                    <div className="orders-list__edit-tags">
                      {row.tags.map((tag) => (
                        <Tag size="small" key={tag} text={tag} />
                      ))}
                    </div>
                  }
                  icon={<Icon onClick={() => editTags(row)} name="tag" />}
                  maxWidth="200px"
                />
              );
            },
          },
          ...(addPaymentColumn
            ? ([
                {
                  dataKey: 'preferredPaymentMethod',
                  label: '',
                  disableLink: true,
                  cellRenderer: (row) => (
                    <Icon
                      className="orders-list__edit-tags"
                      name="euro-sign"
                      onClick={() => {
                        setSelectedOrder(row);
                        setDisplayPaymentModal('create');
                      }}
                    />
                  ),
                },
              ] as ColumnContent<Order>[])
            : []),
        ]}
      />
      {/* Add USERS TO ORDER */}
      <ControlledModal<Order>
        open={!!selectedOrder && displayUsersModal}
        model={selectedOrder}
        onClose={() => {
          setSelectedOrder(undefined);
          setDisplayUsersModal(false);
        }}
        title={t.model.order.addUserToOrder}
        size="small"
      >
        {(controller) => <EditOrderUser controller={controller} />}
      </ControlledModal>
      {/* Edit Cancel At */}
      <ControlledModal<Order>
        open={displayEditCancelAt}
        model={orders[0]}
        onClose={() => {
          setDisplayEditCancelAt(false);
        }}
        title={t.model.order.editCancelAt}
        size="small"
      >
        {(controller) => <EditCancelAt controller={controller} />}
      </ControlledModal>
      {/* UPDATE TAGS */}
      <ControlledModal<Partial<Order>>
        open={!!selectedOrder && displayTagsModal}
        model={{} as Order}
        onClose={() => {
          setSelectedOrder(undefined);
          setDisplayTagsModal(false);
        }}
        title={t.model.order.massUpdateTags}
        size="small"
      >
        {(controller) => <>{selectedOrder && <UpdateOrderTag order={selectedOrder} controller={controller} />}</>}
      </ControlledModal>
      {/* Payment Modal */}
      <ControlledModal<PaymentModel>
        open={!!selectedOrder && !!displayPaymentModal}
        onClose={() => {
          setSelectedOrder(undefined);
          setDisplayPaymentModal(undefined);
        }}
        title={t.actions?.[displayPaymentModal === 'create' ? 'add' : 'edit'](t.model.payment._name)}
        size="small"
        model={{
          provider: selectedOrderPayment
            ? selectedOrderPayment.paymentMethod
            : selectedOrder && selectedOrder.preferredPaymentMethod,
          amount: selectedOrderPayment ? selectedOrderPayment.amount : selectedOrder && selectedOrder.balance,
          status: selectedOrderPayment ? selectedOrderPayment.status : 'paid',
          bankAccount: selectedOrderPayment ? selectedOrderPayment.bankAccount : undefined,
        }}
      >
        {(controller) => (
          <PaymentModal
            controller={controller}
            orderId={selectedOrder && selectedOrder.id}
            mode={displayPaymentModal}
            paymentId={selectedOrderPayment ? selectedOrderPayment.id : undefined}
          />
        )}
      </ControlledModal>
      {selectedPayment?.id && (
        <RefundPaymentModal
          payment={{
            id: selectedPayment.id,
            amountRemaining: selectedPayment.amountRemaining,
            orderBalance: orders[0].balance,
            orderReference: orders[0].reference,
          }}
          onClose={(refunded) => {
            if (refunded) {
              onDocumentChange?.();
            }
            setSelectedPaymentId(null);
          }}
        />
      )}
    </>
  );
}
